import { Alert, Button, Form, Input, InputNumber, Modal, Select, Space, Table, Tooltip, message } from 'antd';
import { get } from 'lodash';
import moment from 'moment';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from 'react-apollo';
import { ADMIN_INVESTMENT_MINIMUMS, INVESTORS } from '../../pages/apollo_queries';
import {
  ADMIN_REMOVE_INVESTMENT_MINIMUM,
  ADMIN_SET_INVESTMENT_MINIMUM,
} from '../../pages/apollo_mutations';
import { TableMainLabel, TableSubLabel } from '../styled';
import { InvestorSelect, investorLabel } from './InvestorSelect';
import {
  MONEY_INPUT,
  PRODUCT_OPTIONS,
  REASON_RULES,
  backendMessage,
  fmtMoney,
  personName,
  productLabel,
} from './shared';

// Tab 2 — the exceptions in force (`adminInvestmentMinimums`), one per person x product. Grant / edit /
// remove all key by the PERSON's userId and carry a mandatory reason; the audit trail stays in the DB.

// `grantedBy` is an admin user id; the name comes from `adminUsers(q: id)` (one cached query per admin).
function AdminName({ id }) {
  const { data } = useQuery(INVESTORS, { variables: { q: id, count: 1, page: 1 }, skip: !id });
  const admin = get(data, 'adminUsers.items[0]');
  if (!id) return '—';
  return admin ? <Tooltip title={admin.email}>{personName(admin)}</Tooltip> : id;
}

const minimumRule = {
  validator: (_, value) =>
    typeof value === 'number' && value >= 0
      ? Promise.resolve()
      : Promise.reject(new Error('Minimum must be 0 or more')),
};

// Grant (no `row`) or update (`row` = the exception being edited: investor and product are fixed).
function ExceptionModal({ row, onClose, onSaved }) {
  const editing = Boolean(row);
  const [form] = Form.useForm();
  const [error, setError] = useState(null);
  const [setMinimum, { loading }] = useMutation(ADMIN_SET_INVESTMENT_MINIMUM);

  const submit = async (values) => {
    setError(null);
    const data = {
      userId: editing ? row.userId : values.userId,
      productType: editing ? row.productType : values.productType,
      minimum: values.minimum,
      reason: values.reason.trim(),
    };
    try {
      await setMinimum({ variables: { data } });
      await onSaved();
      message.success(editing ? 'Exception updated' : 'Exception granted');
      onClose();
    } catch (e) {
      setError(backendMessage(e));
    }
  };

  return (
    <Modal
      visible
      title={editing ? `Edit exception · ${personName(row)}` : 'New exception'}
      okText={editing ? 'Save' : 'Grant exception'}
      okButtonProps={{ loading }}
      onOk={() => form.submit()}
      onCancel={onClose}
      destroyOnClose
    >
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}
      <Form
        form={form}
        layout="vertical"
        onFinish={submit}
        preserve={false}
        initialValues={editing ? { minimum: row.minimum, reason: '' } : { reason: '' }}
        onValuesChange={() => setError(null)}
      >
        {editing ? (
          <>
            <Form.Item label="Investor">
              <Input value={investorLabel(row)} disabled />
            </Form.Item>
            <Form.Item label="Product">
              <Input value={productLabel(row.productType)} disabled />
            </Form.Item>
          </>
        ) : (
          <>
            <Form.Item
              name="userId"
              label="Investor"
              rules={[{ required: true, message: 'Pick an investor' }]}
            >
              <InvestorSelect autoFocus />
            </Form.Item>
            <Form.Item
              name="productType"
              label="Product"
              rules={[{ required: true, message: 'Pick a product' }]}
            >
              <Select options={PRODUCT_OPTIONS} placeholder="Product" />
            </Form.Item>
          </>
        )}
        <Form.Item
          name="minimum"
          label="Minimum"
          extra="Wins over the product rule. 0 = this investor may invest any amount."
          rules={[minimumRule]}
        >
          <InputNumber {...MONEY_INPUT} />
        </Form.Item>
        <Form.Item name="reason" label="Reason (audited)" rules={REASON_RULES}>
          <Input.TextArea rows={3} placeholder="e.g. Institutional client, approved by the TL" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function RemoveModal({ row, onClose, onDone }) {
  const [form] = Form.useForm();
  const [error, setError] = useState(null);
  const [remove, { loading }] = useMutation(ADMIN_REMOVE_INVESTMENT_MINIMUM);

  const submit = async ({ reason }) => {
    setError(null);
    try {
      await remove({
        variables: {
          data: { userId: row.userId, productType: row.productType, reason: reason.trim() },
        },
      });
      await onDone();
      message.success('Exception removed');
      onClose();
    } catch (e) {
      setError(backendMessage(e));
    }
  };

  return (
    <Modal
      visible
      title="Remove exception"
      okText="Remove"
      okButtonProps={{ danger: true, loading }}
      onOk={() => form.submit()}
      onCancel={onClose}
      destroyOnClose
    >
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message={`${investorLabel(row)} goes back to the ${productLabel(row.productType)} rule (current exception: ${fmtMoney(row.minimum)}). The withdrawal is audited with your admin id and the reason.`}
      />
      <Form form={form} layout="vertical" onFinish={submit} preserve={false}>
        <Form.Item name="reason" label="Reason (audited)" rules={REASON_RULES}>
          <Input.TextArea rows={3} autoFocus placeholder="e.g. Exception no longer needed" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

const columns = (setModal) => [
  {
    title: 'Investor',
    render: (row) => (
      <Link to={`/users/${row.userId}`}>
        <TableMainLabel>{personName(row)}</TableMainLabel>
        <TableSubLabel>{row.email}</TableSubLabel>
      </Link>
    ),
  },
  { title: 'Product', dataIndex: 'productType', render: productLabel },
  {
    title: 'Minimum',
    dataIndex: 'minimum',
    align: 'right',
    render: (minimum) =>
      minimum === 0 ? <Tooltip title="Any amount">{fmtMoney(0)}</Tooltip> : fmtMoney(minimum),
  },
  {
    title: 'Since',
    dataIndex: 'grantedAt',
    render: (t) =>
      t ? <Tooltip title={moment(t).format('LLL')}>{moment(t).fromNow()}</Tooltip> : '—',
  },
  { title: 'Granted by', dataIndex: 'grantedBy', render: (id) => <AdminName id={id} /> },
  { title: 'Reason', dataIndex: 'reason', render: (reason) => reason || '—' },
  {
    width: 150,
    render: (row) => (
      <Space>
        <Button size="small" onClick={() => setModal({ mode: 'edit', row })}>
          Edit
        </Button>
        <Button size="small" danger onClick={() => setModal({ mode: 'remove', row })}>
          Remove
        </Button>
      </Space>
    ),
  },
];

export function ExceptionsByInvestor() {
  const [userId, setUserId] = useState(undefined);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(20);
  const [modal, setModal] = useState(null);
  const { data, loading, error, refetch } = useQuery(ADMIN_INVESTMENT_MINIMUMS, {
    variables: { page, count, userId },
    fetchPolicy: 'network-only',
  });

  const items = get(data, 'adminInvestmentMinimums.items', []);
  const total = get(data, 'adminInvestmentMinimums.total', 0);

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginBottom: 16 }}>
        <InvestorSelect
          style={{ width: 420 }}
          placeholder="Filter by investor (name, email or id)"
          value={userId}
          onChange={(id) => {
            setUserId(id || undefined);
            setPage(1);
          }}
        />
        <Button type="primary" onClick={() => setModal({ mode: 'new' })}>
          New exception
        </Button>
      </div>
      {error ? (
        <Alert type="error" showIcon message={backendMessage(error)} />
      ) : (
        <Table
          rowKey={(row) => `${row.userId}:${row.productType}`}
          size="middle"
          columns={columns(setModal)}
          dataSource={items}
          loading={loading}
          locale={{
            emptyText: userId ? 'This investor has no exception in force' : 'No exceptions in force',
          }}
          pagination={{
            total,
            current: page,
            pageSize: count,
            position: ['bottomCenter'],
            hideOnSinglePage: true,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (n) => `${n} exceptions`,
            onChange: (next, size) => {
              setPage(size !== count ? 1 : next);
              setCount(size);
            },
          }}
        />
      )}
      {modal && modal.mode !== 'remove' && (
        <ExceptionModal row={modal.row} onClose={() => setModal(null)} onSaved={refetch} />
      )}
      {modal && modal.mode === 'remove' && (
        <RemoveModal row={modal.row} onClose={() => setModal(null)} onDone={refetch} />
      )}
    </>
  );
}
